import {
  faCopy,
  faGear,
  faLayerGroup,
  faQuestion,
  faRectangleXmark,
  faShareFromSquare,
  faTrashCan,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Col, Dropdown, DropdownButton, Row } from "react-bootstrap";
import { apiJsonRequest } from "../../../utis/apiRequest";
import CustomTagsInput from "../../CommonComponents/Tags/CustomTagsInput";
import CustomTooltip from "../../Elements/Tooltip";
import styles from "./linkCard.module.css";
import { networkService } from "../../config/axios";
import UserInfoContext from "../../../utis/userInfoContext";

const LinkCard = ({
  data,
  trigerCopyLaserLinkEvent,
  triggerSettingsLaserLinkEvent,
  triggerDeleteLaserLinkEvent,
  updateLinkData,
  triggerUdateRecord,
}) => {
  const [kdfsTagsEditStatus, setKdfsTagsEditStatus] = useState(false);
  const [getCopyTextStatus, setCopyTextStatus] = useState(false);
  const [editableTitle, setEditableTitle] = useState({
    status: false,
    value: data?.name,
    id: data?.id,
    docId: data?.doc?.id,
  });
  const editableRef = useRef(null);
  const { setShowNotifications } = useContext(UserInfoContext);

  const handleAddKDFTagsEvent = useCallback(
    async (value) => {
      if (value.length > data?.tags.length) {
        let updateTagsToDoc = await apiJsonRequest(`tags.json`, "POST", {
          name: value[value.length - 1],
        });
        if (updateTagsToDoc?.data != undefined) {
          let vaueToUpdate = [
            ...data?.tags?.map((item) => item.id),
            updateTagsToDoc.data.id,
          ];
          updateLinkData(data, vaueToUpdate);
        }
      } else {
        updateLinkData(
          data,
          data?.tags
            .filter((item) => value.includes(item.name))
            ?.map((item) => item.id)
        );
      }
    },
    [data, updateLinkData]
  );

  const handleCopyTextEvent = useCallback(async (value) => {
    if (!value) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        // fallback for older browsers
        const textarea = document.createElement("textarea");
        textarea.value = value;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      setCopyTextStatus(true);
      setTimeout(() => setCopyTextStatus(false), 3000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  }, []);

  const handleUpdateEditableTitleEvent = useCallback((e) => {
    setEditableTitle((prev) => ({ ...prev, value: e.target.value }));
  }, []);

  useEffect(() => {
    setCopyTextStatus(false);
  }, []);

  const handleRemoveExistingTag = useCallback(
    (tagToRemove) => {
      const remainingTagIds = (data?.tags || [])
        .filter((item) => item.id !== tagToRemove.id)
        .map((item) => item.id);
      updateLinkData(data, remainingTagIds);
    },
    [data, updateLinkData]
  );

  const handleOutsideOrEnter = useCallback(async () => {
    try {
      if (editableRef.current != null) {
        const { data: responseData, status } = await networkService(
          `links/${editableTitle?.id}.json`,
          "PATCH",
          {
            doc_id: editableTitle?.docId,
            id: editableTitle?.id,
            name: editableRef?.current?.value,
          }
        );
        if (status == 200) {
          setEditableTitle((prev) => ({ ...prev, status: false }));
          triggerUdateRecord();
          setShowNotifications({
            showToast: true,
            toastType: "success",
            toastMessage: "Changes Saved",
          });
        }
      }
    } catch (error) {
      if (error.response?.status == 422) {
        setEditableTitle((prev) => ({
          ...prev,
          status: false,
          value: data?.title,
        }));
        setShowNotifications({
          showToast: true,
          toastType: "danger",
          toastMessage:
            "Sorry, Laser Link title must be unique. This title already exists.",
        });
      }
    }
  }, [editableTitle, data, triggerUdateRecord, setShowNotifications]);

  useEffect(() => {
    if (!editableTitle.status) return;

    const handleClickOutside = (event) => {
      if (editableRef.current && !editableRef.current.contains(event.target)) {
        handleOutsideOrEnter();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        handleOutsideOrEnter();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [editableTitle.status, handleOutsideOrEnter]);

  return (
    <div className={styles.linkWrapper}>
      <div className={styles.linkHeader}>
        <Row className="align-items-center">
          <Col md={8}>
            <div className={styles.docTitle}>
              {data?.template && (
                <CustomTooltip
                  title={<p className="p-1 text-light">Template LaserLINK</p>}
                >
                  <FontAwesomeIcon icon={faLayerGroup} />
                </CustomTooltip>
              )}
              {editableTitle.status ? (
                <input
                  type="text"
                  value={editableTitle?.value}
                  ref={editableRef}
                  onChange={(e) => handleUpdateEditableTitleEvent(e)}
                  maxLength={40}
                />
              ) : (
                <strong
                  onClick={() =>
                    setEditableTitle((prev) => ({ ...prev, status: true }))
                  }
                >
                  {data?.name}
                </strong>
              )}
            </div>
          </Col>
          <Col
            md={4}
            className={`d-flex justify-content-between align-items-center ${styles.actionWrapper}`}
          >
            <CustomTooltip
              title={<p className="p-1 text-light">Clone this LaserLINK</p>}
            >
              {/* <FontAwesomeIcon icon={faCopy} onClick={() => trigerCopyLaserLinkEvent(data.id)} className="cursor-pointer fs-6" /> */}
              <img
                src="/icons/copy.svg"
                alt="Share"
                className="cursor-pointer"
                onClick={() => trigerCopyLaserLinkEvent(data.id)}
              />
            </CustomTooltip>
            <CustomTooltip title={<p className="p-1 text-light">Settings</p>}>
              <img
                src="/icons/settings.svg"
                className="cursor-pointer"
                alt="Share"
                onClick={() => triggerSettingsLaserLinkEvent(data)}
              />
            </CustomTooltip>
            <CustomTooltip
              title={<p className="p-1 text-light">Remove LaserLINK</p>}
            >
              {/* <FontAwesomeIcon icon={faTrashCan} className="cursor-pointer fs-6" onClick={() => triggerDeleteLaserLinkEvent(data.id)} /> */}
              <img
                src="/icons/delete.svg"
                className="cursor-pointer"
                alt="Share"
                onClick={() => triggerDeleteLaserLinkEvent(data.id)}
              />
            </CustomTooltip>

            <div className={styles.share}>
              {!data?.template && data?.language_flag?.class && (
                <CustomTooltip
                  title={
                    <p className="p-1 text-light">
                      {data?.language_flag?.name ?? "Language"}
                    </p>
                  }
                >
                  <div
                    className={`${styles.languageFlagMini} ${data?.language_flag?.class}`}
                  ></div>
                </CustomTooltip>
              )}

              {!data?.template && (
                <DropdownButton
                  id={`dropdown-button-drop-${"up"}`}
                  drop={"up"}
                  variant="none"
                  // title={ <><FontAwesomeIcon icon={faShareFromSquare} className="active" /> &nbsp;Share&nbsp; </>}
                  title={
                    <>
                      {" "}
                      <img src="/icons/ios_share.svg" alt="Share" />
                     <span>&nbsp;Share&nbsp;{" "}</span> 
                    </>
                  }
                  className="shareButtonIcon"
                >
                  <Dropdown.Item
                    href={`https://www.facebook.com/sharer/sharer.php?u=${data?.tracking_link}`}
                    target="_blank"
                  >
                    {/* <FontAwesomeIcon icon={faFacebookF} />  */}
                    Facebook
                  </Dropdown.Item>
                  <Dropdown.Item
                    href={`https://twitter.com/intent/tweet?text=${data?.tracking_link}`}
                    target="_blank"
                  >
                    {/* <FontAwesomeIcon icon={faTwitter} />  */}
                    Twitter
                  </Dropdown.Item>
                  <Dropdown.Item
                    href={`mailto:?subject=Check%20this%20out&body=${data?.tracking_link}`}
                    target="_blank"
                  >
                    {/* <FontAwesomeIcon icon={faEnvelope} />  */}
                    Email
                  </Dropdown.Item>
                  <Dropdown.Item
                    href={`https://www.linkedin.com/shareArticle?mini=true&url=https://powered-by.kinetizine.com/MTczMTkwODAwOA/en-US&source=LinkedIn`}
                    target="_blank"
                  >
                    {/* <FontAwesomeIcon icon={faLinkedin} /> */}
                    LinkedIn
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() =>
                      handleCopyTextEvent(data?.tracking_link ?? "")
                    }
                  >
                    <strong style={{ marginLeft: 0 }}>{getCopyTextStatus ? "Link Copied!" : "Copy Link"}</strong>
                  </Dropdown.Item>
                </DropdownButton>
              )}
            </div>
          </Col>
        </Row>
      </div>
      <div id="link-list-top" className={styles.linkBody}>
        <Row className="align-items-center">
          <Col md={(data?.thumb || data?.thumbnail) ? 9 : 12} className={styles.linkBodyCol}>
            <div className={`${styles.docContent}`}>
              <CustomTooltip
                title={
                  <p className="p-1 text-light">
                    Display all LaserLINX for this KDF
                  </p>
                }
              >
                <span className="text-link-success">KDF: </span>
              </CustomTooltip>
              <CustomTooltip
                title={<p className="p-1 text-light">KDF Settings</p>}
              >
                <a
                  href={`/kdfs/${data?.doc?.id}`}
                  className="text-link-success"
                >
                  {" "}
                  {data?.doc?.title}
                </a>
              </CustomTooltip>
            </div>
            <div className={`${styles.docContent} my-2`}>
              <div className="d-flex">
                <span>ET: </span>
                <CustomTooltip
                  title={<p className="p-1 text-light">Engagement Threshold</p>}
                >
                  <span>{data?.page_viewed_sec ?? 0} secs.</span>
                </CustomTooltip>
                {data?.domain && (
                  <CustomTooltip
                    title={
                      <p className="p-1 text-light">
                        Your chosen domain aims to enhance recipient trust and
                        engagement.
                      </p>
                    }
                  >
                    <p className="color-blue mx-1">{data?.domain ?? " "}</p>
                  </CustomTooltip>
                )}
                {data?.has_optin && (
                  <CustomTooltip
                    title={
                      <p className="p-1 text-light">
                        {data?.tooltip_expire_time}
                      </p>
                    }
                  >
                    <span>Access Gate</span>
                  </CustomTooltip>
                )}
              </div>
              <CustomTooltip
                title={
                  <p className="p-1 text-light">
                    Your chosen domain aims to ehance recipient trust and
                    engagement.
                  </p>
                }
              >
                <strong className="text-link-primary">
                  {" "}
                  {data?.doc?.domain}
                </strong>
              </CustomTooltip>
            </div>
            <div className={`${styles.docContent} my-2`}>
              <span className="">Created by: </span>
              <CustomTooltip
                title={
                  <p className="p-1 text-light">{data?.date_created ?? "-"}</p>
                }
              >
                <strong> {data?.username}</strong>
              </CustomTooltip>
            </div>
            {data?.receivers?.length > 0 && (
              <div className={`${styles.docContent} my-2`}>
                <span className="">Recipient: </span>
                <CustomTooltip
                  title={
                    <p className="p-1 text-light">
                      {data?.receivers[0]?.email ?? "-"}
                    </p>
                  }
                >
                  <a href={`/tl-stats/${data.token}#/views`}>
                    <strong>
                      {" "}
                      {data?.receivers[0]?.name}{" "}
                      {data?.receivers?.length > 1 &&
                        `(and ${data?.receivers?.length - 1} more)`}{" "}
                    </strong>
                  </a>
                </CustomTooltip>
              </div>
            )}
            <div
              className="custom-tag-wrapper kdf-settings"
              onClick={() => !kdfsTagsEditStatus && setKdfsTagsEditStatus(true)}
            >
              <span id="kdf-tags" className={styles.tagContent}>
                {kdfsTagsEditStatus ? (
                  <>
                    <div className="tag-input-row">
                    <CustomTagsInput
                      tags={data?.tags || []}
                      placeholderText="Add a new tag..."
                      autoFocus
                      handleAddTags={(value) => {
                        handleAddKDFTagsEvent([...(data?.tags || []), value]);
                        setKdfsTagsEditStatus(false);
                      }}
                      handleRemoveTags={(value) => {
                        handleAddKDFTagsEvent([
                          ...(data?.tags?.filter((it) => it != value) || []),
                        ]);
                        setKdfsTagsEditStatus(false);
                      }}
                    />
                    </div>
                    <div className="tag-actions-row">
                    {/* <CustomTooltip
                      title={
                        <p className="p-1 text-light">
                          You can add a tag by pressing enter, tab, delimiting
                          with comma or clicking outside the tags area
                        </p>
                      }
                    >
                      <img src="/icons/info-color.svg" />
                    </CustomTooltip> */}
                    {/* <img
                      src="/icons/cancel.svg"
                      className="cursor-pointer"
                      onClick={(event) => {
                        event.stopPropagation();
                        setKdfsTagsEditStatus(false);
                      }}
                    /> */}
                    </div>
                  </>
                ) : data?.tags?.length > 0 ? (
                  <>
                    <span className={styles.tagLabel}>Tags:</span>
                    <span id="kt-tag" className={styles.tagsDisplay}>
                      {data?.tags?.map((tag, tagInd) => (
                        <span
                          key={tagInd}
                          className="tag-primary text-white mx-1"
                        >
                          {tag.name}
                          <button
                            type="button"
                            className="tag-remove-btn"
                            aria-label={`Remove tag ${tag.name}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              handleRemoveExistingTag(tag);
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      <span
                        role="button"
                        tabIndex={0}
                        className={`${styles.tagAddButton}`}
                        aria-label="Add tag"
                        onClick={(event) => {
                          event.stopPropagation();
                          setKdfsTagsEditStatus(true);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setKdfsTagsEditStatus(true);
                          }
                        }}
                      >
                        <img src="/icons/add_2.svg" width="14" />
                      </span>
                    </span>
                  </>
                ) : (
                  <span
                    className={`d-flex align-items-center custom-tag-element mb-0 ${styles.tagPlaceholder}`}
                  >
                    Add LaserLink tag
                    <img style={{marginLeft: '8px'}} src="/icons/add_2.svg" width="14" />
                  </span>
                )}
              </span>
            </div>
          </Col>
          {(data?.thumb || data?.thumbnail) && (
            <Col md={3} className="d-flex align-items-center justify-content-center">
              <a href={data?.viewer_preview} target="_blank" rel="noopener noreferrer" className={styles.laserlinkImgList}>
                <img src={data?.thumb || data?.thumbnail} alt={data?.name} />
              </a>
            </Col>
          )}
        </Row>
      </div>
      {data?.stats?.viewers != 0 ? (
        <div className={styles.linkFooter}>
                <Row>
                  <div className={styles.stats}>
                    {!data?.template ? (
                      <>
                        <a
                          href={`/tl-stats/${data?.token}#/views`}
                          className={styles.statsButton}
                        >
                          <strong className="d-block">
                            {data?.stats?.viewers}
                          </strong>
                          <span>Visits</span>
                        </a>
                        {data?.stats?.optins?.length > 0 && (
                          <a
                            href={`/tl-stats/${data?.token}#/views`}
                            className={styles.statsButton}
                          >
                            <strong className="d-block">
                              {data?.stats?.optins?.length}
                            </strong>
                            <span>
                              Opt-in{data?.stats?.optins?.length > 1 && "s"}
                            </span>
                          </a>
                        )}
                        <CustomTooltip
                          title={
                            <p className="p-1 text-light">
                              {data?.link_total_sec_viewed}
                            </p>
                          }
                        >
                          <a
                            href={`/tl-stats/${data?.token}#/at-a-glance`}
                            className={styles.statsButton}
                          >
                            <strong className="d-block">
                              {data?.stats?.percent_viewed}
                            </strong>
                            <span>Viewed</span>
                          </a>
                        </CustomTooltip>
                        {data?.last_viewed && (
                          <a
                            href={`/tl-stats/${data.token}#/at-a-glance`}
                            className={styles.statsButton}
                          >
                            <strong className="d-block">
                              {data?.last_viewed}
                            </strong>
                            <span>Last Viewed</span>
                          </a>
                        )}
                      </>
                    ) : (
                      ""
                    )}
                  </div>
                </Row>
        </div>
      ) : null}

    </div>
    // </Container>
  );
};
export default React.memo(LinkCard);
